import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  TextField,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_ASSET_SCOPE } from "../../apis/settingsApis/assets-apis/allAssetsApis";
import { GET_USER_DETAILS } from "../../apis/dashboard-apis/dashboardDetailApi";
import ConfirmSnackbar from "../../components/Snackbar/ConfirmSnackbar";
import { ASSETS_OUT_OF_SCOPE_EFFECT } from "../../utils/enums";

/**
 * Bulk scope dialog for assets, in both directions. `mode`:
 *   "out" -> Mark selected Out of Scope (reason box)
 *   "in"  -> Mark selected In Scope (reason is cleared server-side)
 * Direction is fixed by the button that opened it, so there's no radio toggle.
 * Styling intentionally matches the People page's BulkScopeDialog.
 */
const BulkAssetScopeDialog = ({
  open,
  onClose,
  assets = [],
  mode = "out",
  onSaveSuccess,
  refetch,
}) => {
  const isInScope = mode === "in";
  const [updateAssetScope] = useMutation(UPDATE_ASSET_SCOPE);
  const { data: userData } = useQuery(GET_USER_DETAILS);
  const currentUserName = userData?.getAccountDetails?.userDetails?.name || "";

  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [scopeReason, setScopeReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setScopeReason("");
  }, [open]);

  // Scope lives on the physical device (assets.assets, keyed by serial), so the
  // same device selected from two sources — WatchTower and an MDM — is one call,
  // not two. Dedupe by assetId and count devices, not rows.
  const targets = useMemo(() => {
    const seen = new Map();
    assets.forEach((a) => {
      if (a?.assetId && !seen.has(a.assetId)) seen.set(a.assetId, a);
    });
    return [...seen.values()];
  }, [assets]);

  // The box is optional here, matching the People page, but the API rejects a
  // blank reason — so fall back to naming the admin who did it.
  const defaultOutOfScopeReason = () =>
    `Marked out of scope by ${currentUserName?.trim() || "admin"}`;

  const handleSave = async () => {
    if (!targets.length) return;

    const reason = isInScope ? null : scopeReason.trim() || defaultOutOfScopeReason();

    setSaving(true);
    try {
      // updateAssetScope is per-asset, so fan out and report on the whole batch.
      // allSettled, not all: one rejected device must not hide the ones that worked.
      const results = await Promise.allSettled(
        targets.map((a) =>
          updateAssetScope({
            variables: { assetId: a.assetId, inScope: isInScope, scopeReason: reason },
          }).then(({ data }) => {
            const res = data?.updateAssetScope;
            if (res?.success === false) {
              throw new Error(res.message || "Failed to update scope");
            }
            return res;
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected");
      const succeeded = results.length - failed.length;
      const direction = isInScope ? "in scope" : "out of scope";

      await refetch?.();

      if (failed.length === 0) {
        setSnackbarInfo({
          message: `${succeeded} device${succeeded === 1 ? "" : "s"} marked ${direction}.`,
          vertical: "top",
          horizontal: "right",
        });
        onSaveSuccess?.();
        onClose();
        return;
      }

      // Partial failure: keep the dialog open so the selection isn't lost and the
      // admin can retry the ones that didn't take.
      setSnackbarInfo({
        message: succeeded
          ? `${succeeded} of ${results.length} devices marked ${direction}. ${failed.length} failed: ${failed[0].reason?.message || "unknown error"}`
          : `Failed to mark devices ${direction}: ${failed[0].reason?.message || "unknown error"}`,
        vertical: "top",
        horizontal: "right",
      });
      if (succeeded) onSaveSuccess?.();
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to update compliance scope",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1.5,
            pt: 2.5,
            px: 3,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h5" fontWeight={600} color="#111827">
            {isInScope ? "Mark In Scope" : "Mark Out of Scope"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#6b7280" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2, "&.MuiDialogContent-root": { pt: 2.5 } }}>
          <Alert severity="info" sx={{ mb: isInScope ? 0 : 3 }}>
            <strong>{targets.length}</strong> selected device
            {targets.length === 1 ? "" : "s"} will be marked{" "}
            {isInScope ? "in scope" : "out of scope"}.{" "}
            {isInScope
              ? "Security checks will start running for them again."
              : ASSETS_OUT_OF_SCOPE_EFFECT}

          </Alert>

          {targets.length < assets.length && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {assets.length} selected rows cover {targets.length} physical device
              {targets.length === 1 ? "" : "s"} — scope applies per device, across
              every source it reports from.
            </Alert>
          )}

          {!isInScope && (
            <TextField
              fullWidth
              label="Additional Notes (optional)"
              value={scopeReason}
              onChange={(e) => setScopeReason(e.target.value)}
              disabled={saving}
              multiline
              rows={3}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              placeholder="Add any notes about why these devices are out of scope..."
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{ color: "#6b7280", fontWeight: 500, "&:hover": { backgroundColor: "#f3f4f6" } }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || targets.length === 0}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default BulkAssetScopeDialog;
